import { Switch, Route, Router } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import HomePage from "@/pages/Home";
import CalendarPage from "@/pages/Calendar";
import CheckoutPage from "@/pages/Checkout";
import FAQPage from "@/pages/FAQ";
import UnsubscribedPage from "@/pages/Unsubscribed";
import GuidePage from "@/pages/Guide";
import TeamsIndex from "@/pages/TeamsIndex";
import TeamPage from "@/pages/TeamPage";
import GroupsIndex from "@/pages/GroupsIndex";
import GroupPage from "@/pages/GroupPage";
import PlatformsIndex from "@/pages/PlatformsIndex";
import PlatformPage from "@/pages/PlatformPage";
import NotFound from "@/pages/not-found";
import HreflangTags from "@/components/HreflangTags";
import OgLocaleTags from "@/components/OgLocaleTags";
import JsonLdLanguage from "@/components/JsonLdLanguage";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { stripLocaleFromPath } from "@/lib/locale-utils";

/**
 * Custom hook for wouter that strips locale prefix from the URL path
 * so routes are defined without locale prefixes (e.g. "/faq" not "/es/faq").
 * The locale is read from the URL path by i18n's urlPath detector.
 */
function useLocaleAwareLocation(): [string, (to: string) => void] {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const basePath = stripLocaleFromPath(path);

  const navigate = (to: string) => {
    // For internal navigation, preserve the locale prefix
    window.location.assign(to);
  };

  return [basePath, navigate];
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HreflangTags />
      <OgLocaleTags />
      <JsonLdLanguage />
      <Router hook={useLocaleAwareLocation}>
        <Switch>
          <Route path="/" component={HomePage} />
          <Route path="/calendar" component={CalendarPage} />
          <Route path="/checkout" component={CheckoutPage} />
          <Route path="/faq" component={FAQPage} />
          <Route path="/guide" component={GuidePage} />
          <Route path="/teams" component={TeamsIndex} />
          <Route path="/teams/:year(\d{4})/:teamSlug" component={TeamPage} />
          <Route path="/teams/:teamSlug" component={TeamPage} />
          <Route path="/groups" component={GroupsIndex} />
          <Route path="/groups/:year(\d{4})/:letter" component={GroupPage} />
          <Route path="/groups/:letter" component={GroupPage} />
          <Route path="/platforms" component={PlatformsIndex} />
          <Route path="/platforms/:platformSlug" component={PlatformPage} />
          <Route path="/unsubscribed" component={UnsubscribedPage} />
          <Route component={NotFound} />
        </Switch>
      </Router>
      <Toaster />
    </QueryClientProvider>
  );
}
